import { useEffect, useState } from "react";
import { supabase, CLINIC_ID } from "./supabase.js";
import { friendlyError } from "./lib/format.js";

const MONTHS = [
  "Januari","Februari","Maret","April","Mei","Juni",
  "Juli","Agustus","September","Oktober","November","Desember",
];
const YEARS = Array.from({ length: 4 }, (_, i) => new Date().getFullYear() - 1 + i);

const STATUS_OPTIONS = ["HADIR","IZIN","SAKIT","ALPHA"];
const STATUS_TONE = {
  HADIR:  "status-positive",
  Hadir:  "status-positive",
  PULANG: "status-positive",
  Pulang: "status-positive",
  IZIN:   "status-warning",
  Izin:   "status-warning",
  SAKIT:  "status-info",
  Sakit:  "status-info",
  ALPHA:  "status-danger",
  Alpha:  "status-danger",
};

function fmt(iso) {
  if (!iso) return "—";
  const d = new Date(iso);
  return d.toLocaleTimeString("id-ID", { hour: "2-digit", minute: "2-digit" });
}

function fmtDate(str) {
  if (!str) return "—";
  const [y, m, d] = str.split("-");
  return `${d}/${m}/${y}`;
}

function attId(staffId, dateStr) {
  return `ATT-${staffId}-${dateStr.replace(/-/g, "")}`;
}

export function KehadiranPage() {
  const now = new Date();
  const [month, setMonth] = useState(now.getMonth() + 1);
  const [year, setYear]   = useState(now.getFullYear());

  const [staff, setStaff]           = useState([]);
  const [rows, setRows]             = useState([]);
  const [loading, setLoading]       = useState(true);
  const [error, setError]           = useState("");

  // Form tambah manual
  const [showForm, setShowForm]     = useState(false);
  const [form, setForm]             = useState({ nakes_id: "", attendance_date: "", status: "HADIR", notes: "" });
  const [saving, setSaving]         = useState(false);

  // Edit inline
  const [editingId, setEditingId]   = useState(null);
  const [editStatus, setEditStatus] = useState("");
  const [editNotes, setEditNotes]   = useState("");

  useEffect(() => { fetchData(); }, [month, year]); // eslint-disable-line

  async function fetchData() {
    setLoading(true);
    setError("");
    try {
      const startDate = `${year}-${String(month).padStart(2,"0")}-01`;
      const endDate   = month === 12
        ? `${year + 1}-01-01`
        : `${year}-${String(month + 1).padStart(2,"0")}-01`;

      const [staffRes, attRes] = await Promise.all([
        supabase.from("staff")
          .select("id,name,payroll_type,division")
          .eq("clinic_id", CLINIC_ID)
          .eq("status", "ACTIVE")
          .order("name"),
        supabase.from("attendance")
          .select("id,nakes_id,attendance_date,status,allowance_amount,check_in_at,check_out_at,notes")
          .eq("clinic_id", CLINIC_ID)
          .gte("attendance_date", startDate)
          .lt("attendance_date", endDate)
          .order("attendance_date", { ascending: false }),
      ]);
      if (staffRes.error) throw staffRes.error;
      if (attRes.error)   throw attRes.error;

      setStaff(staffRes.data || []);
      // Enrich with staff name
      const staffMap = Object.fromEntries((staffRes.data || []).map(s => [s.id, s]));
      setRows((attRes.data || []).map(r => ({
        ...r,
        staffName: staffMap[r.nakes_id]?.name || r.nakes_id,
        payroll_type: staffMap[r.nakes_id]?.payroll_type || "—",
      })));
    } catch (err) {
      setError(friendlyError(err));
    } finally {
      setLoading(false);
    }
  }

  // ── Tambah manual ──
  async function handleAdd(e) {
    e.preventDefault();
    if (!form.nakes_id || !form.attendance_date) return;
    setSaving(true);
    try {
      const id = attId(form.nakes_id, form.attendance_date);
      const staffMember = staff.find(s => s.id === form.nakes_id);
      const { error: ie } = await supabase.from("attendance").upsert({
        id,
        clinic_id:        CLINIC_ID,
        nakes_id:         form.nakes_id,
        attendance_date:  form.attendance_date,
        status:           form.status,
        allowance_amount: form.status === "HADIR" ? (staffMember?.allowance_per_attendance || 0) : 0,
        notes:            form.notes || null,
      }, { onConflict: "id" });
      if (ie) throw ie;
      setForm({ nakes_id: "", attendance_date: "", status: "HADIR", notes: "" });
      setShowForm(false);
      await fetchData();
    } catch (err) {
      setError(friendlyError(err));
    } finally {
      setSaving(false);
    }
  }

  // ── Edit status inline ──
  function startEdit(row) {
    setEditingId(row.id);
    setEditStatus(row.status);
    setEditNotes(row.notes || "");
  }

  async function saveEdit(row) {
    try {
      const staffMember = staff.find(s => s.id === row.nakes_id);
      const { error: ue } = await supabase.from("attendance").update({
        status:           editStatus,
        allowance_amount: editStatus === "HADIR" ? (staffMember?.allowance_per_attendance || 0) : 0,
        notes:            editNotes || null,
      }).eq("id", row.id);
      if (ue) throw ue;
      setEditingId(null);
      await fetchData();
    } catch (err) {
      setError(friendlyError(err));
    }
  }

  // ── Summary per nakes ──
  // "Hadir" (login saja) + "Pulang" (login+logout) + "HADIR" = semua dihitung hadir
  const isHadir = (r) => /^(hadir|pulang)$/i.test(r.status || "");
  const is      = (row, val) => (row.status || "").toUpperCase() === val;
  const summary = staff.map(s => {
    const nakesRows = rows.filter(r => r.nakes_id === s.id);
    return {
      ...s,
      hadir: nakesRows.filter(isHadir).length,
      izin:  nakesRows.filter(r => is(r, "IZIN")).length,
      sakit: nakesRows.filter(r => is(r, "SAKIT")).length,
      alpha: nakesRows.filter(r => is(r, "ALPHA")).length,
    };
  });

  return (
    <div className="page-stack">

      {/* ── Header ── */}
      <article className="panel">
        <div className="panel-head">
          <div>
            <span className="eyebrow">SDM · XV Portal</span>
            <h3>Log Kehadiran</h3>
          </div>
          <div style={{ display:"flex", gap:10, alignItems:"flex-end" }}>
            <div>
              <label style={{ fontSize:12, fontWeight:600, color:"#6b6555", display:"block", marginBottom:4 }}>Periode</label>
              <div style={{ display:"flex", gap:8 }}>
                <select value={month} onChange={e => setMonth(Number(e.target.value))} style={selStyle}>
                  {MONTHS.map((m,i) => <option key={i} value={i+1}>{m}</option>)}
                </select>
                <select value={year} onChange={e => setYear(Number(e.target.value))} style={selStyle}>
                  {YEARS.map(y => <option key={y} value={y}>{y}</option>)}
                </select>
              </div>
            </div>
            <button className="button button-secondary" onClick={() => setShowForm(v => !v)}>
              {showForm ? "Tutup Form" : "+ Tambah Manual"}
            </button>
          </div>
        </div>

        {error && <div className="alert alert-danger"><strong>Error.</strong> {error}</div>}

        {/* Form tambah manual */}
        {showForm && (
          <form onSubmit={handleAdd} style={{ marginTop:16, display:"flex", gap:12, flexWrap:"wrap", alignItems:"flex-end" }}>
            <div style={{ display:"flex", flexDirection:"column", gap:4, flex:2, minWidth:160 }}>
              <label style={lblStyle}>Nakes / Staf</label>
              <select value={form.nakes_id} onChange={e => setForm(p=>({...p,nakes_id:e.target.value}))} style={inputStyle} required>
                <option value="">-- pilih --</option>
                {staff.map(s => <option key={s.id} value={s.id}>{s.name}</option>)}
              </select>
            </div>
            <div style={{ display:"flex", flexDirection:"column", gap:4, flex:2, minWidth:140 }}>
              <label style={lblStyle}>Tanggal</label>
              <input type="date" value={form.attendance_date}
                onChange={e => setForm(p=>({...p,attendance_date:e.target.value}))}
                style={inputStyle} required />
            </div>
            <div style={{ display:"flex", flexDirection:"column", gap:4, flex:1, minWidth:120 }}>
              <label style={lblStyle}>Status</label>
              <select value={form.status} onChange={e => setForm(p=>({...p,status:e.target.value}))} style={inputStyle}>
                {STATUS_OPTIONS.map(s => <option key={s} value={s}>{s}</option>)}
              </select>
            </div>
            <div style={{ display:"flex", flexDirection:"column", gap:4, flex:3, minWidth:160 }}>
              <label style={lblStyle}>Catatan</label>
              <input value={form.notes} onChange={e => setForm(p=>({...p,notes:e.target.value}))}
                placeholder="opsional" style={inputStyle} />
            </div>
            <button type="submit" className="button button-primary" disabled={saving}>
              {saving ? "Menyimpan…" : "Simpan"}
            </button>
          </form>
        )}
      </article>

      {/* ── Ringkasan per Nakes ── */}
      <article className="panel">
        <div className="panel-head">
          <div>
            <span className="eyebrow">Rekap Bulan Ini</span>
            <h3>Ringkasan Kehadiran — {MONTHS[month-1]} {year}</h3>
          </div>
        </div>
        <table className="mt-table">
          <thead>
            <tr>
              <th>NAMA</th>
              <th>TIPE</th>
              <th style={{textAlign:"center",color:"#1a6b41"}}>HADIR</th>
              <th style={{textAlign:"center",color:"#b45309"}}>IZIN</th>
              <th style={{textAlign:"center",color:"#1d4ed8"}}>SAKIT</th>
              <th style={{textAlign:"center",color:"#991b1b"}}>ALPHA</th>
              <th style={{textAlign:"center"}}>TOTAL HARI</th>
            </tr>
          </thead>
          <tbody>
            {summary.map(s => (
              <tr key={s.id}>
                <td><strong>{s.name}</strong></td>
                <td><span className="status status-neutral">{s.payroll_type || "—"}</span></td>
                <td style={{textAlign:"center"}}><strong style={{color:"#1a6b41"}}>{s.hadir}</strong></td>
                <td style={{textAlign:"center"}}>{s.izin  || "—"}</td>
                <td style={{textAlign:"center"}}>{s.sakit || "—"}</td>
                <td style={{textAlign:"center"}}>{s.alpha || "—"}</td>
                <td style={{textAlign:"center"}}>{s.hadir + s.izin + s.sakit + s.alpha}</td>
              </tr>
            ))}
            {!summary.length && (
              <tr><td colSpan={7} className="muted" style={{padding:"16px 12px"}}>Tidak ada data.</td></tr>
            )}
          </tbody>
        </table>
      </article>

      {/* ── Log Detail ── */}
      <article className="panel table-panel" style={{ padding: '1.5rem' }}>
        <div className="panel-head">
          <div>
            <span className="eyebrow">Detail</span>
            <h3>Log Absensi</h3>
          </div>
        </div>
        {loading ? (
          <div className="content-loading"><div className="loader" /><span>Memuat…</span></div>
        ) : (
          <table className="mt-table">
            <thead>
              <tr>
                <th>TANGGAL</th>
                <th>NAMA</th>
                <th>CHECK-IN</th>
                <th>CHECK-OUT</th>
                <th>STATUS</th>
                <th>CATATAN</th>
                <th>AKSI</th>
              </tr>
            </thead>
            <tbody>
              {rows.map(row => (
                <tr key={row.id}>
                  <td>{fmtDate(row.attendance_date)}</td>
                  <td><strong>{row.staffName}</strong></td>
                  <td style={{fontFamily:"monospace",fontSize:13}}>{fmt(row.check_in_at)}</td>
                  <td style={{fontFamily:"monospace",fontSize:13}}>{fmt(row.check_out_at)}</td>
                  <td>
                    {editingId === row.id ? (
                      <select value={editStatus} onChange={e => setEditStatus(e.target.value)} style={{...inputStyle,padding:"4px 8px"}}>
                        {STATUS_OPTIONS.map(s => <option key={s} value={s}>{s}</option>)}
                      </select>
                    ) : (
                      <span className={`status ${STATUS_TONE[(row.status||"").toUpperCase()] || "status-neutral"}`}>{row.status}</span>
                    )}
                  </td>
                  <td style={{color:"#6b6555",fontSize:13}}>
                    {editingId === row.id
                      ? <input value={editNotes} onChange={e => setEditNotes(e.target.value)} style={{...inputStyle,padding:"4px 8px"}} placeholder="catatan" />
                      : (row.notes || "—")}
                  </td>
                  <td>
                    {editingId === row.id ? (
                      <>
                        <button className="link-button" onClick={() => saveEdit(row)}>Simpan</button>
                        {" "}
                        <button className="link-button link-danger" onClick={() => setEditingId(null)}>Batal</button>
                      </>
                    ) : (
                      <button className="link-button" onClick={() => startEdit(row)}>Edit</button>
                    )}
                  </td>
                </tr>
              ))}
              {!rows.length && !loading && (
                <tr><td colSpan={7} className="muted" style={{padding:"16px 12px"}}>Belum ada log untuk periode ini.</td></tr>
              )}
            </tbody>
          </table>
        )}
      </article>

      <style>{`
        .mt-table { width:100%; border-collapse:collapse; font-size:14px; }
        .mt-table th { text-align:left; font-size:11px; text-transform:uppercase; letter-spacing:.04em; color:#7a7364; padding:10px 12px; border-bottom:1px solid #e8e2d5; }
        .mt-table td { padding:12px; border-bottom:1px solid #f1ece0; }
        .link-button { background:none; border:none; padding:0; font-size:13px; font-weight:600; color:#2f6f4f; cursor:pointer; }
        .link-danger { color:#c0392b; }
      `}</style>
    </div>
  );
}

const selStyle = { padding:"9px 12px", border:"1px solid #e8e2d5", borderRadius:8, fontSize:14, background:"#fff" };
const inputStyle = { padding:"9px 12px", border:"1px solid #e8e2d5", borderRadius:8, fontSize:14, background:"#fff", width:"100%", boxSizing:"border-box" };
const lblStyle = { fontSize:12, fontWeight:600, color:"#6b6555" };
